
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, User, Mail, Phone, Linkedin, Pencil, Trash } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LeadContact } from "../types";

interface LeadContactsProps {
  leadId: string;
  contacts: LeadContact[];
  onUpdate: () => void;
}

export const LeadContacts = ({ leadId, contacts = [], onUpdate }: LeadContactsProps) => {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editContact, setEditContact] = useState<LeadContact | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    title: "",
    email: "",
    phone: "",
    linkedin_url: "",
    notes: "",
  });
  
  const resetForm = () => {
    setFormData({
      name: "",
      title: "",
      email: "",
      phone: "",
      linkedin_url: "",
      notes: "",
    });
    setEditContact(null);
  };
  
  const handleShowForm = (contact?: LeadContact) => {
    if (contact) {
      setEditContact(contact);
      setFormData({
        name: contact.name,
        title: contact.title || "",
        email: contact.email || "",
        phone: contact.phone || "",
        linkedin_url: contact.linkedin_url || "",
        notes: contact.notes || "",
      });
    } else {
      resetForm();
    }
    setShowForm(true);
  };
  
  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Contact name is required");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (editContact) {
        const { error } = await supabase
          .from("lead_contacts")
          .update({
            name: formData.name,
            title: formData.title || null,
            email: formData.email || null,
            phone: formData.phone || null,
            linkedin_url: formData.linkedin_url || null,
            notes: formData.notes || null,
          })
          .eq("id", editContact.id);
          
        if (error) throw error;
        
        toast.success("Contact updated successfully");
      } else {
        const { error } = await supabase
          .from("lead_contacts")
          .insert({
            lead_id: leadId,
            name: formData.name,
            title: formData.title || null,
            email: formData.email || null,
            phone: formData.phone || null,
            linkedin_url: formData.linkedin_url || null,
            notes: formData.notes || null,
          });
          
        if (error) throw error;
        
        toast.success("Contact added successfully");
      }
      
      setShowForm(false);
      resetForm();
      onUpdate();
      
    } catch (error) {
      console.error("Error saving contact:", error);
      toast.error("Failed to save contact");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleDelete = async (contactId: string) => {
    try {
      const { error } = await supabase
        .from("lead_contacts")
        .delete()
        .eq("id", contactId);
        
      if (error) throw error;
      
      toast.success("Contact deleted successfully");
      onUpdate();
      
    } catch (error) {
      console.error("Error deleting contact:", error);
      toast.error("Failed to delete contact");
    }
  };
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Contacts</CardTitle>
        <Button onClick={() => handleShowForm()}>
          <Plus className="mr-2 h-4 w-4" />
          Add Contact
        </Button>
      </CardHeader>
      <CardContent>
        {contacts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No contacts yet. Add your first contact for this lead.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {contacts.map((contact) => (
              <Card key={contact.id} className="overflow-hidden">
                <div className="p-4 flex flex-col md:flex-row md:items-center gap-4 justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-primary" />
                      <span className="font-medium">{contact.name}</span>
                      {contact.title && <span className="text-muted-foreground">{contact.title}</span>}
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {contact.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                            {contact.email}
                          </a>
                        </div>
                      )}
                      
                      {contact.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <a href={`tel:${contact.phone}`} className="text-primary hover:underline">
                            {contact.phone}
                          </a>
                        </div>
                      )}
                      
                      {contact.linkedin_url && (
                        <div className="flex items-center gap-2">
                          <Linkedin className="h-4 w-4 text-muted-foreground" />
                          <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                            LinkedIn Profile
                          </a>
                        </div>
                      )}
                    </div>
                    
                    {contact.notes && (
                      <div className="text-sm text-muted-foreground">
                        {contact.notes}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 self-end md:self-center">
                    <Button variant="outline" size="icon" onClick={() => handleShowForm(contact)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" className="text-destructive hover:text-destructive" onClick={() => handleDelete(contact.id)}>
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
      
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editContact ? "Edit Contact" : "Add New Contact"}</DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="linkedin_url">LinkedIn URL</Label>
              <Input
                id="linkedin_url"
                value={formData.linkedin_url}
                onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : editContact ? "Update Contact" : "Add Contact"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
